import gsap from 'gsap';

export class HamburgerService {
  #BUTTON: HTMLButtonElement | null = null;
  #BAR_1: Element | null = null;
  #BAR_2: Element | null = null;
  #BAR_3: Element | null = null;

  #IS_OPEN = false;
  #OVERLAY: HTMLDivElement | null = null;
  #HAMBURGER_MENU: HTMLDivElement | null = null;

  constructor() {
    this.#BUTTON = document.querySelector('.btn-hamburger');
    this.#HAMBURGER_MENU = document.querySelector('#hamburger-menu');

    if (!this.#BUTTON || !this.#HAMBURGER_MENU) {
      return;
    }

    this.#BAR_1 = this.#BUTTON.querySelector('.bar-1');
    this.#BAR_2 = this.#BUTTON.querySelector('.bar-2');
    this.#BAR_3 = this.#BUTTON.querySelector('.bar-3');

    if (!this.#BAR_1 || !this.#BAR_2 || !this.#BAR_3) {
      return;
    }

    gsap.set(this.#HAMBURGER_MENU, {
      x: '100%',
      overflowY: 'auto',
    });

    this.#addClickHandler();
    this.#addHoverEffects();
    this.#addResizeHandler();
  }

  #addClickHandler(): void {
    if (!this.#BUTTON) {
      return;
    }

    this.#BUTTON.addEventListener('click', (): void => {
      if (this.#IS_OPEN) {
        this.#closeMenu();
        return;
      }

      this.#openMenu();
    });
  }

  #addHoverEffects(): void {
    if (!this.#BUTTON) {
      return;
    }

    this.#BUTTON.addEventListener('mouseenter', (): void => {
      if (this.#IS_OPEN) {
        return;
      }

      gsap.to(this.#BAR_1, {
        attr: {
          d: 'M4 6h16',
        },
        duration: 0.3,
        ease: 'power2.out',
      });

      gsap.to(this.#BAR_2, {
        attr: {
          d: 'M4 12h16',
        },
        duration: 0.3,
        ease: 'power2.out',
      });
    });

    this.#BUTTON.addEventListener('mouseleave', (): void => {
      if (this.#IS_OPEN) {
        return;
      }

      gsap.to(this.#BAR_1, {
        attr: {
          d: 'M4 6h8',
        },
        duration: 0.3,
        ease: 'power2.out',
      });

      gsap.to(this.#BAR_2, {
        attr: {
          d: 'M4 12h12',
        },
        duration: 0.3,
        ease: 'power2.out',
      });
    });
  }

  #addResizeHandler(): void {
    window.addEventListener('resize', (): void => {
      if (window.innerWidth >= 768 && this.#IS_OPEN) {
        this.#closeMenu();
      }
    });
  }

  #createOverlay(): void {
    this.#OVERLAY = document.createElement('div');
    this.#OVERLAY.className = 'fixed inset-0 bg-black-20 backdrop-blur-xl z-40';
    this.#OVERLAY.style.opacity = '0';
    document.body.appendChild(this.#OVERLAY);

    this.#OVERLAY.addEventListener('click', (): void => this.#closeMenu());
  }

  #removeOverlay(): void {
    if (this.#OVERLAY && this.#OVERLAY.parentNode) {
      this.#OVERLAY.parentNode.removeChild(this.#OVERLAY);
      this.#OVERLAY = null;
    }
  }

  #openMenu(): void {
    this.#IS_OPEN = true;

    document.body.style.overflow = 'hidden';

    this.#createOverlay();

    if (this.#OVERLAY) {
      gsap.to(this.#OVERLAY, {
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out',
      });
    }

    if (this.#BAR_1) {
      gsap.to(this.#BAR_1, {
        attr: { d: 'M6 6l12 12' },
        duration: 0.3,
        ease: 'power2.out',
      });
    }

    if (this.#BAR_2) {
      gsap.to(this.#BAR_2, {
        opacity: 0,
        duration: 0.2,
        ease: 'power2.out',
      });
    }

    if (this.#BAR_3) {
      gsap.to(this.#BAR_3, {
        attr: { d: 'M6 18l12-12' },
        duration: 0.3,
        ease: 'power2.out',
      });
    }

    if (this.#HAMBURGER_MENU) {
      gsap.to(this.#HAMBURGER_MENU, {
        x: 0,
        duration: 0.4,
        delay: 0.2,
        ease: 'power3.out',
        onStart: (): void => {
          this.#HAMBURGER_MENU?.classList.remove('hidden');
        },
      });
    }
  }

  #closeMenu(): void {
    this.#IS_OPEN = false;

    document.body.style.overflow = '';

    if (this.#HAMBURGER_MENU) {
      gsap.to(this.#HAMBURGER_MENU, {
        x: '100%',
        duration: 0.4,
        ease: 'power3.in',
        onComplete: (): void => {
          this.#HAMBURGER_MENU?.classList.add('hidden');
        },
      });
    }

    if (this.#OVERLAY) {
      gsap.to(this.#OVERLAY, {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: (): void => {
          this.#removeOverlay();
        },
      });
    }

    const isHovered: boolean | undefined = this.#BUTTON?.matches(':hover');

    if (this.#BAR_1) {
      gsap.to(this.#BAR_1, {
        attr: { d: isHovered ? 'M4 6h16' : 'M4 6h8' },
        duration: 0.3,
        ease: 'power2.out',
      });
    }

    if (this.#BAR_2) {
      gsap.to(this.#BAR_2, {
        opacity: 1,
        duration: 0,
        ease: 'power2.out',
      });

      gsap.to(this.#BAR_2, {
        attr: { d: isHovered ? 'M4 12h16' : 'M4 12h12' },
        duration: 0.3,
        ease: 'power2.out',
      });
    }

    if (this.#BAR_3) {
      gsap.to(this.#BAR_3, {
        attr: { d: 'M4 18h16' },
        duration: 0.3,
        ease: 'power2.out',
      });
    }
  }
}
